import React, { useState, useEffect } from "react";
import ReactTable from "react-table";
import { Button, UncontrolledTooltip } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import s from "./styles.scss";
import {
  faEdit,
  faUserEdit,
  faFileExport,
  faUndo,
  faListAlt,
  faExclamationCircle
} from "@fortawesome/free-solid-svg-icons";
import MembresiaSnapshot from "../../../../types/MembresiaSnapshot";
import { formatIsoDate, formatMoney } from "../../../helpers/formatters";

export interface TableProps {
  page: Page<MembresiaSnapshot>;
  loading: boolean;
  onFetchData: ({ page, pageSize }) => void;
  onExport: () => void;
  onActualizarSnapshot: (row: MembresiaSnapshot) => void;
  onCambiarVendedor: (row: MembresiaSnapshot) => void;
  onDescartarCambios: (row: MembresiaSnapshot) => void;
}

export const MemoTable = React.memo(
  ({
    page,
    loading,
    onFetchData,
    onExport,
    onActualizarSnapshot,
    onCambiarVendedor,
    onDescartarCambios
  }: TableProps) => {
    const [modalCambiosOpen, setModalCambiosOpen] = useState(false);
    const [
      snapshotSeleccionado,
      setSnapshotSeleccionado
    ] = useState<MembresiaSnapshot | null>(null);
    const [columns, setColumns] = useState(
      columnsFormatter(
        onActualizarSnapshot,
        onCambiarVendedor,
        onDescartarCambios,
        (row: MembresiaSnapshot) => {
          setSnapshotSeleccionado(row);
          setModalCambiosOpen(true);
        }
      )
    );
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);

    useEffect(() => {
      setColumns(
        columnsFormatter(
          onActualizarSnapshot,
          onCambiarVendedor,
          onDescartarCambios,
          (row: MembresiaSnapshot) => {
            setSnapshotSeleccionado(row);
            setModalCambiosOpen(true);
          }
        )
      );
    }, [onActualizarSnapshot, onCambiarVendedor, onDescartarCambios]);

    useEffect(() => {
      if (page) {
        setPageNumber(page.number || 0);
        setTotalPages(page.totalPages || 0);
      }
    }, [page]);

    return (
      <div>
        <div className="row">
          {!!page && !!page.content && !!page.content.length && (
            <>
              <div className="col-6 p-0" style={{ lineHeight: "40px" }}>
                <span>
                  &nbsp;&nbsp;&nbsp;&nbsp;Membresías mostradas:{" "}
                  {page.numberOfElements} de {page.totalElements}
                </span>
              </div>
              <div
                className="col-6 p-0 text-right"
                style={{ lineHeight: "40px" }}
              >
                <Button
                  id="btnReportExportCsv"
                  color="black"
                  type="button"
                  onClick={onExport}
                >
                  Exportar <FontAwesomeIcon icon={faFileExport} color="black" />
                </Button>
              </div>
            </>
          )}
        </div>
        <ReactTable
          manual
          data={page ? page.content : []}
          columns={columns}
          pages={totalPages}
          loading={loading}
          onFetchData={onFetchData}
          page={pageNumber}
          defaultPageSize={100}
          showPageSizeOptions={false}
          className="-striped -highlight"
          previousText="Anterior"
          nextText="Siguiente"
          loadingText="Cargando..."
          noDataText="No hay registros"
          pageText="Página"
          ofText="de"
          rowsText="filas"
        />
      </div>
    );
  }
);

function columnsFormatter(
  onActualizarSnapshot: (row: MembresiaSnapshot) => void,
  onCambiarVendedor: (row: MembresiaSnapshot) => void,
  onDescartarCambios: (row: MembresiaSnapshot) => void,
  onMostrarCambios: (row: MembresiaSnapshot) => void
) {
  return [
    {
      Header: "Cotización",
      id: "idCotizacionItem",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 80,
      accessor: simpleAccessor("idCotizacionItem")
    },
    {
      Header: "Periodo",
      id: "periodo",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 100,
      accessor: dateAccessor("periodo")
    },
    {
      Header: "Factura",
      id: "folioFactura",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 96,
      accessor: simpleAccessor("folioFactura")
    },
    {
      Header: "Vendedor",
      id: "vendedor",
      className: s.tdClear,
      headerClassName: "text-left",
      accessor: simpleAccessor("vendedor")
    },
    {
      Header: "Operación",
      id: "tipoOperacion",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 104,
      accessor: row => {
        switch (row.tipoOperacion) {
          case "VENTA_MEMBRESIA":
            return <div className={fieldClassName(row)}>Venta</div>;
          case "REACTIVACION":
            return <div className={fieldClassName(row)}>Reactivación</div>;
          case "INTEGRANTE_INDIVIDUAL":
            return <div className={fieldClassName(row)}>Integrante</div>;
          default:
            return <div className={fieldClassName(row)}>No reconocida</div>;
        }
      }
    },
    {
      Header: "Membresía",
      id: "membresiaLink",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 150,
      accessor: (row: MembresiaSnapshot) => {
        const href = `/membresias-socios/${row.idMembresiaSocio}?idClub=${row.idClubBase}&idPublico=${row.idPublico}`;
        const label = `${row.idPublico} de ${row.clubBase}`;

        return (
          <div className={fieldClassName(row)}>
            <a href={href} target="_blank" rel="noreferrer">
              {label}
            </a>
          </div>
        );
      }
    },
    {
      Header: "Tipo",
      id: "tipoMembresia",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 104,
      accessor: (row: MembresiaSnapshot) => {
        return (
          <div className={fieldClassName(row)}>
            {(row.membresia || "NA").replace("Membresía ", "")}&nbsp;
          </div>
        );
      }
    },
    {
      Header: "Precio Venta",
      id: "precioVenta",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 100,
      accessor: moneyAccessor("precioVenta")
    },
    {
      Header: "Modalidad",
      id: "modo",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 120,
      accessor: (row: MembresiaSnapshot) => {
        let descripcionModo;
        switch (row.modo) {
          case "DESCARTADA":
            descripcionModo = "Descartada";
            break;
          case "ALCANCE":
            descripcionModo = "Alcance";
            break;
          default:
            descripcionModo = "Estándar";
        }

        return (
          <div className={fieldClassName(row)}>{descripcionModo}&nbsp;</div>
        );
      }
    },
    {
      Header: "Con cambios",
      id: "tieneCambios",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 64,
      accessor: booleanAccessor("tieneCambios")
    },
    {
      Header: "Acciones",
      id: "acciones",
      className: s.tdClear,
      headerClassName: "text-left",
      width: 64,
      accessor: (row: MembresiaSnapshot) => {
        const btnId = `snapshot-${row.idCotizacionItem}`;
        const disabled = !!row.cotizacionEliminada;
        const alerta = "Esta cotización ha sido eliminada";
        const color = disabled ? "#777777" : "#4e779b";
        const undoColor = disabled ? "#777777" : "#8b0000";
        const verCambiosColor = "#17a2b8";

        return (
          <div className={fieldClassName(row)}>
            {renderBoton(
              row,
              `${btnId}-vendedor`,
              !disabled,
              onCambiarVendedor,
              !disabled ? "Cambiar Vendedor" : alerta,
              color,
              faUserEdit
            )}
          </div>
        );
      }
    }
  ];
}

function simpleAccessor(key: keyof MembresiaSnapshot) {
  return (row: MembresiaSnapshot) => {
    return <div className={fieldClassName(row)}>{row[key]}&nbsp;</div>;
  };
}

function booleanAccessor(key: keyof MembresiaSnapshot) {
  return (row: MembresiaSnapshot) => {
    return (
      <div className={fieldClassName(row)}>
        {!!row[key] ? "Sí" : "No"}&nbsp;
      </div>
    );
  };
}

function dateAccessor(key: keyof MembresiaSnapshot) {
  return (row: MembresiaSnapshot) => {
    const fecha = row[key] ? formatIsoDate(row[key] as string) : "";
    return <div className={fieldClassName(row)}>{fecha}&nbsp;</div>;
  };
}

function moneyAccessor(accessor: string) {
  return row => {
    const cantidad = Number(row[accessor]);

    return (
      <div className={fieldClassName(row) + " text-right"}>
        {!Number.isNaN(cantidad) && row[accessor] != null
          ? formatMoney(cantidad, 2)
          : "-"}
      </div>
    );
  };
}

function renderIconoIncumplimiento(row: MembresiaSnapshot, id: string) {
  const razones = row.razonesIncumplimiento || [];
  const tooltip =
    razones.length > 0
      ? razones.map((razon, idx) => <div key={idx}>• {razon}</div>)
      : "No cumple con los requisitos de comisionabilidad";

  return (
    <span className={s.info} id={id}>
      <FontAwesomeIcon icon={faExclamationCircle} color="#ffffff" />
      <UncontrolledTooltip placement="left" target={id}>
        <div style={{ textAlign: "left" }}>
          <strong>Incumplimiento:</strong>
          {tooltip}
        </div>
      </UncontrolledTooltip>
    </span>
  );
}

function renderBoton(
  row: MembresiaSnapshot,
  id,
  habilitado,
  action,
  tooltip,
  color,
  icon
) {
  if (habilitado) {
    return (
      <span id={id} onClick={() => action(row)} className={s.btn}>
        <FontAwesomeIcon icon={icon} color={color} />
        <UncontrolledTooltip placement="left" target={id}>
          {tooltip}
        </UncontrolledTooltip>
      </span>
    );
  } else {
    return (
      <span className={s.info} id={id}>
        <FontAwesomeIcon icon={icon} color={color} />
        <UncontrolledTooltip placement="left" target={id}>
          {tooltip}
        </UncontrolledTooltip>
      </span>
    );
  }
}

function fieldClassName(row: MembresiaSnapshot) {
  let className = s.field;
  return className;
}
